import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Building2, Briefcase, Shield, ArrowRight, ArrowLeft } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const SelectUserTypePage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const userTypes = [
    {
      id: 'individual',
      icon: User,
      titleAr: 'أفراد',
      titleEn: 'Individuals',
      descriptionAr: 'دخول العملاء الأفراد لمتابعة قضاياهم واستشاراتهم القانونية',
      descriptionEn: 'Individual clients login to track their cases and legal consultations',
      buttonTextAr: 'دخول الأفراد',
      buttonTextEn: 'Individual Login',
      bgGradient: 'from-wazin-navy/5 to-wazin-gold/5',
      borderColor: 'border-wazin-gold/30',
      iconBg: 'bg-gradient-to-br from-wazin-gold to-yellow-600',
      buttonBg: 'bg-wazin-navy hover:bg-wazin-navy/90',
      link: '/login?type=individual'
    },
    {
      id: 'business',
      icon: Building2,
      titleAr: 'أعمال / شركات',
      titleEn: 'Business / Corporate',
      descriptionAr: 'دخول الشركات والمؤسسات لإدارة عقودها وقضاياها وخدماتها القانونية',
      descriptionEn: 'Corporate login to manage contracts, cases and legal services',
      buttonTextAr: 'دخول الأعمال',
      buttonTextEn: 'Business Login',
      bgGradient: 'from-wazin-gold/5 to-wazin-navy/5',
      borderColor: 'border-wazin-navy/30',
      iconBg: 'bg-gradient-to-br from-wazin-navy to-wazin-navy/80',
      buttonBg: 'bg-wazin-gold hover:bg-wazin-gold/90 text-wazin-navy',
      link: '/login?type=business'
    },
    {
      id: 'employee',
      icon: Briefcase,
      titleAr: 'موظفو وازن',
      titleEn: 'Wazin Employees',
      descriptionAr: 'دخول المحامين والموظفين للوحات التحكم الداخلية',
      descriptionEn: 'Lawyers and staff access to internal control panels',
      buttonTextAr: 'دخول الموظفين',
      buttonTextEn: 'Employee Login',
      bgGradient: 'from-wazin-navy/5 to-gray-100',
      borderColor: 'border-wazin-gold/30',
      iconBg: 'bg-gradient-to-br from-wazin-gold/80 to-wazin-gold',
      buttonBg: 'bg-wazin-navy hover:bg-wazin-navy/90',
      link: '/login?type=employee'
    },
    {
      id: 'admin',
      icon: Shield,
      titleAr: 'إدارة وازن',
      titleEn: 'Wazin Management',
      descriptionAr: 'دخول الإدارة العليا لإدارة الصلاحيات، التقارير، وإعدادات المنصة',
      descriptionEn: 'Management access for permissions, reports, and platform settings',
      buttonTextAr: 'دخول الإدارة',
      buttonTextEn: 'Admin Login',
      bgGradient: 'from-gray-50 to-wazin-gold/5',
      borderColor: 'border-wazin-navy/30',
      iconBg: 'bg-gradient-to-br from-wazin-navy/90 to-black',
      buttonBg: 'bg-gradient-to-r from-wazin-gold to-yellow-600 hover:from-wazin-gold/90 hover:to-yellow-600/90 text-wazin-navy',
      link: '/login?type=admin'
    }
  ];

  const handleTypeSelect = (link) => {
    navigate(link);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-wazin-navy/5 relative overflow-hidden">
      {/* Wazin Watermark - Fixed Background */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
        <img 
          src="/images/wazin-logo.png" 
          alt="Wazin Watermark" 
          className="w-[600px] md:w-[900px] lg:w-[1100px] h-auto object-contain select-none opacity-[0.05]"
          style={{ 
            filter: 'grayscale(100%) brightness(85%)',
            mixBlendMode: 'multiply'
          }}
        />
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-5 pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-wazin-gold rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-40 w-80 h-80 bg-wazin-navy rounded-full blur-3xl"></div>
      </div>

      <div style={{ position: 'relative', zIndex: 100 }}>
        <Navbar />
      </div>

      <div className="pt-32 pb-20 px-4 relative" style={{ zIndex: 10 }}>
        <div className="max-w-7xl mx-auto">
          {/* Elegant Header with Wazin Branding */}
          <div className="text-center mb-20">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-wazin-gold to-yellow-600 rounded-full blur-xl opacity-50"></div>
                <div className="relative bg-gradient-to-br from-wazin-gold to-yellow-600 rounded-full p-8 shadow-2xl">
                  <img 
                    src="/wazin-icon-gold.png" 
                    alt="Wazin Icon" 
                    className="w-24 h-24 object-contain filter brightness-0 invert"
                  />
                </div>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-wazin-navy mb-6" style={{ fontFamily: 'system-ui' }}>
              {i18n.language === 'ar' ? 'منصة وازن' : 'Wazin Platform'}
            </h1>
            <p className="text-2xl text-gray-700 mb-4 font-semibold">
              {i18n.language === 'ar' 
                ? 'المنصة الموحدة للخدمات القانونية الإلكترونية'
                : 'The Unified Platform for Electronic Legal Services'
              }
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-2">
              {i18n.language === 'ar' 
                ? 'لإدارة القضايا، العقود، الاستشارات والاشتراكات القانونية في مكان واحد'
                : 'To manage cases, contracts, consultations and legal subscriptions in one place'
              }
            </p>
            <p className="text-base text-gray-500 max-w-3xl mx-auto leading-relaxed">
              {i18n.language === 'ar' 
                ? 'نسخّر التقنية لتقديم تجربة قانونية احترافية، موثوقة، وسهلة الاستخدام لعملائنا من الأفراد والأعمال'
                : 'We harness technology to provide a professional, reliable, and easy-to-use legal experience for our individual and business clients'
              }
            </p>
          </div>

          {/* User Type Cards - Horizontal Layout like Najiz */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {userTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.id}
                  onClick={() => handleTypeSelect(type.link)}
                  className="group relative bg-white rounded-2xl border border-gray-200 hover:border-wazin-gold transition-all duration-300 hover:shadow-2xl cursor-pointer overflow-hidden"
                >
                  {/* Top Colored Bar - Wazin Colors */}
                  <div className={`h-2 ${type.iconBg.includes('gradient') ? type.iconBg.replace('bg-gradient-to-br', 'bg-gradient-to-r') : type.iconBg} group-hover:h-3 transition-all duration-300`}></div>

                  {/* Card Content */}
                  <div className="p-6 text-center">
                    {/* Icon with Wazin Colors */}
                    <div className="flex justify-center mb-6">
                      <div className={`${type.iconBg} rounded-2xl p-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-12 h-12 text-white" />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-wazin-navy mb-4 group-hover:text-wazin-gold transition-colors">
                      {i18n.language === 'ar' ? type.titleAr : type.titleEn}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 leading-relaxed mb-6 min-h-[60px]">
                      {i18n.language === 'ar' ? type.descriptionAr : type.descriptionEn}
                    </p>

                    {/* Button with Wazin Colors */}
                    <button
                      className={`w-full ${type.buttonBg} text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-2`}
                    >
                      {i18n.language === 'ar' ? type.buttonTextAr : type.buttonTextEn}
                      {i18n.language === 'ar' ? (
                        <ArrowLeft className="w-4 h-4" />
                      ) : (
                        <ArrowRight className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* Hover Gradient Overlay - Wazin Colors */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${type.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}></div>
                </div>
              );
            })}
          </div>

          {/* Elegant Footer Section */}
          <div className="text-center space-y-6 mt-16">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-full max-w-xs"></div>
            </div>

            {/* Create Account Link */}
            <div className="bg-gradient-to-r from-wazin-gold/5 to-wazin-navy/5 rounded-2xl p-8 max-w-2xl mx-auto border border-wazin-gold/20">
              <p className="text-xl text-gray-700 mb-4 font-semibold">
                {i18n.language === 'ar' ? 'تجربة قانونية استثنائية تنتظرك' : 'An Exceptional Legal Experience Awaits You'}
              </p>
              <p className="text-base text-gray-600 mb-6">
                {i18n.language === 'ar' ? 'ليس لديك حساب؟ انضم إلى منصة وازن القانونية الرائدة' : "Don't have an account? Join the leading Wazin Legal Platform"}
              </p>
              <button
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-wazin-gold to-yellow-600 hover:from-wazin-gold/90 hover:to-yellow-600/90 text-wazin-navy px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-3"
              >
                {i18n.language === 'ar' ? 'إنشاء حساب جديد' : 'Create New Account'}
                {i18n.language === 'ar' ? (
                  <ArrowLeft className="w-5 h-5" />
                ) : (
                  <ArrowRight className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Back to Home */}
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-wazin-gold transition-colors flex items-center gap-2 mx-auto text-base"
            >
              {i18n.language === 'ar' ? (
                <>
                  <ArrowRight className="w-4 h-4" />
                  العودة للصفحة الرئيسية
                </>
              ) : (
                <>
                  <ArrowLeft className="w-4 h-4" />
                  Back to Homepage
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SelectUserTypePage;
